import { createApi } from "@reduxjs/toolkit/query/react";
import { getAxiosInstance } from "../../../api";

const axiosBaseQuery =
  () =>
  async ({ params, endpoint, method, body }) => {
    const api = await getAxiosInstance();
    const getParams = (key) => {
      if (params?.[key]) {
        return `${key}=${params[key]}&`;
      } else {
        return "";
      }
    };

    const branchIdParams = params?.branch_id
      ? params.branch_id.map((id) => `branch_id[]=${id}`).join("&")
      : "";

    try {
      const response = await api[method](
        `${endpoint}?${getParams("remittance_id")}${getParams(
          "sort_by"
        )}sort_order=${
          params?.sort_order || "desc"
        }&page_size=${params?.page_size || "10"}&${getParams(
          "search"
        )}&${branchIdParams}&page=${params?.page || 1}&${getParams(
          "start"
        )}${getParams("end")}${getParams("status")}`,
        body
      );

      return { data: response.data };
    } catch (axiosError) {
      let err = axiosError;
      return {
        data: err?.response?.data || err.message,
      };
    }
  };

export const remittance = createApi({
  reducerPath: "remittanceApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    "remittance",
    "remittanceDetailSummary",
    "remittanceDocuments",
    "remittanceAdditionalRequests",
  ],
  endpoints: (builder) => ({
    getRemittanceListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `admin/remittance`,
      }),
      providesTags: ["remittance"],
    }),
    updateRemittanceTableFields: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `admin/user/fields/update`,
      }),
      invalidatesTags: ["remittance"],
    }),
    getRemittanceDetailSummary: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `admin/remittance/detail-summary`,
      }),
      providesTags: ["remittanceDetailSummary"],
    }),
    getRemittanceApplicationDetail: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `admin/remittance/application-detail`,
      }),
    }),
    getRemittancePaymentDetail: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `admin/remittance/payment-detail`,
      }),
    }),
    getRemittanceBeneficiaryDetail: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `admin/remittance/beneficiary-detail`,
      }),
    }),
    getRemittanceDeclarationDetail: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `admin/remittance/declaration-detail`,
      }),
    }),
    getRemittanceDocuments: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `admin/remittance/documents`,
      }),
      providesTags: ["remittanceDocuments"],
    }),
    updateRemittanceDocumentStatus: builder.mutation({
      query: (body) => ({
        method: "put",
        body,
        endpoint: `admin/remittance/documents/change-status`,
      }),
      invalidatesTags: ["remittanceDocuments"],
    }),
    requestAdditionalDocument: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `admin/remittance/documents/request-additional`,
      }),
      invalidatesTags: ["remittanceAdditionalRequests"],
    }),
    getAdditionalDocumentRequests: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `admin/remittance/documents/additional-requests`,
      }),
      providesTags: ["remittanceAdditionalRequests"],
    }),
    getRemittanceRequestableDocTypes: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `admin/remittance/documents/requestable`,
      }),
    }),
  }),
});

export const {
  useGetRemittanceListDataQuery,
  useUpdateRemittanceTableFieldsMutation,
  useGetRemittanceDetailSummaryQuery,
  useGetRemittanceApplicationDetailQuery,
  useGetRemittancePaymentDetailQuery,
  useGetRemittanceBeneficiaryDetailQuery,
  useGetRemittanceDeclarationDetailQuery,
  useGetRemittanceDocumentsQuery,
  useUpdateRemittanceDocumentStatusMutation,
  useRequestAdditionalDocumentMutation,
  useGetAdditionalDocumentRequestsQuery,
  useGetRemittanceRequestableDocTypesQuery,
} = remittance;
